// Ícones do painel: Phosphor (`@phosphor-icons/react`, peso regular), uma língua só.
//
// Os nomes são os que o `lucide-react` usava (WEB-137 manteve), pra os call sites não mudarem.
// Cada ícone é o glifo do Phosphor mais próximo do significado. Decorativo por default (`aria-hidden`):
// quem carrega o significado é o texto ao lado; sozinho num botão, passe `aria-label`.

import {
  ArrowClockwiseIcon,
  ArrowLineDownIcon,
  ArrowLineUpIcon,
  ArrowSquareOutIcon,
  ArrowsLeftRightIcon,
  ArrowsSplitIcon,
  ArrowUpRightIcon,
  BellIcon,
  BookIcon,
  BookOpenIcon,
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  ChartLineUpIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CodeIcon,
  CopyIcon,
  CpuIcon,
  CreditCardIcon,
  DotsThreeIcon,
  DownloadSimpleIcon,
  EnvelopeSimpleIcon,
  FileArrowUpIcon,
  FileTextIcon,
  FlagIcon,
  FlaskIcon,
  GaugeIcon,
  GearIcon,
  type Icon,
  type IconProps,
  ImageIcon,
  InfoIcon,
  KeyIcon,
  LightningIcon,
  LinkIcon,
  ListIcon,
  LockIcon,
  MagnifyingGlassIcon,
  MoneyIcon,
  MonitorIcon,
  MoonIcon,
  PackageIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  PercentIcon,
  CheckIcon as PhCheckIcon,
  CircleIcon as PhCircleIcon,
  XIcon as PhXIcon,
  PlugIcon,
  PlusIcon,
  ReceiptIcon,
  RepeatIcon,
  ScalesIcon,
  ScanIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  SignOutIcon,
  SlidersHorizontalIcon,
  SquaresFourIcon,
  StorefrontIcon,
  SunIcon,
  TrashIcon,
  TrayIcon,
  UploadSimpleIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  WalletIcon,
  WarningCircleIcon,
  WarningIcon,
  WebhooksLogoIcon,
  WifiSlashIcon,
} from '@phosphor-icons/react';
import type { ReactElement } from 'react';

export type AppIconProps = Omit<IconProps, 'ref' | 'weight'> & {
  /** Aceita e ignora: o traço vem do peso do Phosphor. Só pra chamada antiga continuar compilando. */
  strokeWidth?: number | string;
};

export type AppIcon = (props: AppIconProps) => ReactElement;

/** Decorativo por default; com `aria-label` vira imagem rotulada. */
function ph(Glyph: Icon, weight: IconProps['weight'] = 'regular'): AppIcon {
  return function AppGlyph({ size = 24, strokeWidth: _strokeWidth, 'aria-label': label, ...rest }) {
    if (label === undefined) {
      return <Glyph aria-hidden="true" size={size} weight={weight} {...rest} />;
    }
    return <Glyph role="img" aria-label={label} size={size} weight={weight} {...rest} />;
  };
}

export const ArrowDownToLine = ph(ArrowLineDownIcon);
export const ArrowLeftRight = ph(ArrowsLeftRightIcon);
export const ArrowUpFromLine = ph(ArrowLineUpIcon);
export const ArrowUpRight = ph(ArrowUpRightIcon);
export const Banknote = ph(MoneyIcon);
export const Bell = ph(BellIcon);
export const BookOpen = ph(BookOpenIcon);
export const BookText = ph(BookIcon);
export const ChartLine = ph(ChartLineUpIcon);
// Check e X em bold: os de peso regular ficam leves em 16 px.
export const Check = ph(PhCheckIcon, 'bold');
export const ChevronDown = ph(CaretDownIcon);
export const ChevronLeft = ph(CaretLeftIcon);
export const ChevronRight = ph(CaretRightIcon);
export const ChevronUp = ph(CaretUpIcon);
export const ChevronsUpDown = ph(CaretUpDownIcon);
export const CircleAlert = ph(WarningCircleIcon);
export const CircleCheck = ph(CheckCircleIcon);
export const CircleIcon = ph(PhCircleIcon, 'fill');
export const Code = ph(CodeIcon);
// "Comece aqui" na documentação: bandeira de largada.
export const Compass = ph(FlagIcon);
export const Copy = ph(CopyIcon);
export const Cpu = ph(CpuIcon);
export const Download = ph(DownloadSimpleIcon);
export const ExternalLink = ph(ArrowSquareOutIcon);
export const FileText = ph(FileTextIcon);
export const FileUp = ph(FileArrowUpIcon);
export const FlaskConical = ph(FlaskIcon);
export const Gauge = ph(GaugeIcon);
export const Image = ph(ImageIcon);
export const ImagePlus = ph(ImageIcon);
export const Inbox = ph(TrayIcon);
export const Info = ph(InfoIcon);
export const KeyRound = ph(KeyIcon);
export const LayoutGrid = ph(SquaresFourIcon);
export const Link2 = ph(LinkIcon);
export const List = ph(ListIcon);
export const LoaderCircle = ph(CircleNotchIcon);
export const Lock = ph(LockIcon);
export const LogOut = ph(SignOutIcon);
export const MailPlus = ph(EnvelopeSimpleIcon);
export const Menu = ph(ListIcon);
export const Monitor = ph(MonitorIcon);
export const Moon = ph(MoonIcon);
export const MoreHorizontal = ph(DotsThreeIcon, 'bold');
export const Package = ph(PackageIcon);
export const Pencil = ph(PencilSimpleIcon);
export const Percent = ph(PercentIcon);
export const Plug = ph(PlugIcon);
export const Plus = ph(PlusIcon);
export const Receipt = ph(ReceiptIcon);
export const ReceiptText = ph(ReceiptIcon);
export const Repeat2 = ph(RepeatIcon);
export const RotateCw = ph(ArrowClockwiseIcon);
export const Scale = ph(ScalesIcon);
export const ScanFace = ph(ScanIcon);
export const Search = ph(MagnifyingGlassIcon);
export const SearchX = ph(MagnifyingGlassIcon);
export const Send = ph(PaperPlaneTiltIcon);
export const Settings = ph(GearIcon);
export const Settings2 = ph(SlidersHorizontalIcon);
export const ShieldCheck = ph(ShieldCheckIcon);
export const ShoppingBag = ph(ShoppingBagIcon);
export const Split = ph(ArrowsSplitIcon);
export const Store = ph(StorefrontIcon);
export const Sun = ph(SunIcon);
export const Trash2 = ph(TrashIcon);
export const TriangleAlert = ph(WarningIcon);
export const Upload = ph(UploadSimpleIcon);
export const User = ph(UserIcon);
export const UserPlus = ph(UserPlusIcon);
export const Users = ph(UsersIcon);
export const Wallet = ph(WalletIcon);
export const WalletCards = ph(CreditCardIcon);
export const Webhook = ph(WebhooksLogoIcon);
export const WifiOff = ph(WifiSlashIcon);
export const X = ph(PhXIcon, 'bold');
export const Zap = ph(LightningIcon);

// Nomes duplicados que o lucide também exportava (o shadcn importa a forma com `Icon`).
export const AlertTriangle = TriangleAlert;
export const CheckIcon = Check;
export const ChevronDownIcon = ChevronDown;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;
export const ChevronUpIcon = ChevronUp;
export const Code2 = Code;
export const Key = KeyRound;
export const SearchIcon = Search;
export const XIcon = X;
